import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import StarIcon from '@mui/icons-material/Star';
import StarHalfIcon from '@mui/icons-material/StarHalf';
import { Box, Typography, useTheme } from '@mui/material';
import { useRef, useState, useEffect } from 'react';
import { ReviewWithSummary } from '@/graphql/__generated/sdk';
import TAGButton from '../TAGButton';
import { getHeroReviewsCardContainerStyles, heroReviewsCardContent, ratingHeader, ratingStars, reviewCommentBox, controlsRow, arrowBtn, dotsRow, getDotStyles, dotActiveStyles, getTruncateTextStyles } from './HeroReviews.styles';
import ReviewsListModal from './HeroReviewsListModal';
export function HeroRating({
  rating,
  starSize = 16
}: Readonly<{
  rating: number;
  starSize?: number;
}>) {
  const floorRating = Math.floor(rating);
  const decimal = rating - floorRating;
  const displayFull = decimal >= 0.6 ? floorRating + 1 : floorRating;
  const showHalf = decimal > 0.2 && decimal < 0.6;
  return <Box sx={{
    display: 'flex',
    alignItems: 'center'
  }} data-sentry-element="Box" data-sentry-component="HeroRating" data-sentry-source-file="HeroReviews.tsx">
			{Array.from({
      length: displayFull
    }, (_, i) => <Box key={`star-${rating}-${i}`} sx={{
      display: 'flex',
      alignItems: 'center'
    }}>
					<StarIcon sx={{
        fontSize: starSize
      }} />
				</Box>)}
			{showHalf && <Box sx={{
      display: 'flex',
      alignItems: 'center'
    }}>
					<StarHalfIcon sx={{
        fontSize: starSize
      }} />
				</Box>}
		</Box>;
}
export default function HeroReviews({
  reviews,
  top3Reviews,
  facilityName
}: Readonly<{
  reviews: ReviewWithSummary;
  top3Reviews: {
    reviewId?: string | null;
    comments?: string | null;
  }[];
  facilityName: string;
}>) {
  const theme = useTheme();
  const [currentIndex, setCurrentIndex] = useState(0);
  const commentsContainerRef = useRef<HTMLDivElement | null>(null);
  const isScrollingProgrammaticallyRef = useRef(false);
  const isChangingFromArrowsRef = useRef(false);
  useEffect(() => {
    const container = commentsContainerRef.current;
    if (!container) return;
    const handleIntersect: IntersectionObserverCallback = entries => {
      const best = entries.reduce<{
        idx: number;
        ratio: number;
      } | null>((acc, entry) => {
        const el = entry.target as HTMLElement;
        const idxAttr = el.dataset.reviewIndex;
        if (!idxAttr) return acc;
        const idx = Number(idxAttr);
        const ratio = entry.intersectionRatio;
        return !acc || ratio > acc.ratio ? {
          idx,
          ratio
        } : acc;
      }, null);
      if (best && !isScrollingProgrammaticallyRef.current) {
        setCurrentIndex(best.idx);
      }
    };
    const observer = new IntersectionObserver(handleIntersect, {
      root: container,
      threshold: [0.4, 0.6, 0.8]
    });
    const items = container.querySelectorAll<HTMLElement>('[data-review-index]');
    for (const el of items) observer.observe(el);
    return () => observer.disconnect();
  }, [top3Reviews]);
  useEffect(() => {
    if (!isChangingFromArrowsRef.current) return;
    const container = commentsContainerRef.current;
    if (!container) return;
    const item = container.querySelector<HTMLElement>(`[data-review-index="${currentIndex}"]`);
    if (!item) return;
    isScrollingProgrammaticallyRef.current = true;
    item.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'start'
    });
    const clearFlag = () => {
      isScrollingProgrammaticallyRef.current = false;
      isChangingFromArrowsRef.current = false;
    };

    // Fallback: clear flags if scroll event never fires (e.g. target already in view)
    const fallback = setTimeout(clearFlag, 600);
    const handleScrollEnd = () => {
      clearTimeout(fallback);
      clearFlag();
    };
    container.addEventListener('scroll', handleScrollEnd, {
      once: true
    });
    return () => {
      clearTimeout(fallback);
      clearFlag();
      container.removeEventListener('scroll', handleScrollEnd);
    };
  }, [currentIndex]);

  // Reset carousel position when the review list changes length
  useEffect(() => {
    setCurrentIndex(0);
    isChangingFromArrowsRef.current = false;
    isScrollingProgrammaticallyRef.current = false;
  }, [top3Reviews.length]);
  if (!reviews || reviews.reviewCount === 0) return null;
  if (!top3Reviews || top3Reviews.length === 0) return null;
  return <Box sx={getHeroReviewsCardContainerStyles(theme)} data-sentry-element="Box" data-sentry-component="HeroReviews" data-sentry-source-file="HeroReviews.tsx">
			<Box sx={heroReviewsCardContent} data-sentry-element="Box" data-sentry-source-file="HeroReviews.tsx">
				<Box sx={ratingHeader} data-sentry-element="Box" data-sentry-source-file="HeroReviews.tsx">
					<Box sx={ratingStars} data-sentry-element="Box" data-sentry-source-file="HeroReviews.tsx">
						<Typography variant="bodyLargeBook" data-sentry-element="Typography" data-sentry-source-file="HeroReviews.tsx">{reviews?.averageRating}</Typography>
						<HeroRating rating={Number(reviews?.averageRating || 0)} data-sentry-element="HeroRating" data-sentry-source-file="HeroReviews.tsx" />
					</Box>
					{top3Reviews.length > 1 && <ReviewsListModal facilityName={facilityName} reviews={reviews} />}
				</Box>

				<Typography variant="bodyMediumSemiBold" data-sentry-element="Typography" data-sentry-source-file="HeroReviews.tsx">Featured Reviews</Typography>

				<Box ref={commentsContainerRef} sx={reviewCommentBox} tabIndex={0} data-sentry-element="Box" data-sentry-source-file="HeroReviews.tsx">
					{top3Reviews.map((rev, idx) => <Box key={rev.reviewId ?? idx} data-review-index={idx} sx={getTruncateTextStyles(theme)}>
							<Typography variant="bodySmallBook">{rev?.comments}</Typography>
						</Box>)}
				</Box>

				{top3Reviews.length > 1 && <Box sx={controlsRow}>
						<TAGButton onClick={() => {
          isChangingFromArrowsRef.current = true;
          setCurrentIndex(prev => Math.max(0, prev - 1));
        }} aria-label="Previous review" sx={arrowBtn} disabled={currentIndex === 0} size="small">
							<ArrowBackIcon sx={{
            padding: 0,
            height: 'auto'
          }} />
						</TAGButton>

						<Box sx={dotsRow}>
							{top3Reviews.map((_, idx) => <Box component="span" key={`dot-${idx}`} sx={idx === currentIndex ? dotActiveStyles : getDotStyles(theme)} aria-hidden="true" />)}
						</Box>
						<TAGButton onClick={() => {
          isChangingFromArrowsRef.current = true;
          setCurrentIndex(prev => Math.min(top3Reviews.length - 1, prev + 1));
        }} aria-label="Next review" sx={arrowBtn} disabled={currentIndex === top3Reviews.length - 1} size="small">
							<ArrowForwardIcon sx={{
            padding: 0,
            height: 'auto'
          }} />
						</TAGButton>
					</Box>}
			</Box>
		</Box>;
}